import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { ReferenceSearchDialog, ReferenceSourceTabs } from "src/components/ReferenceSearch";
import type { ProductFormProductIngredient } from "src/pages/Product/ProductForm/ProductForm.types";
import { useDelayedSearch } from "src/utils/useDelayedSearch";

import { useIngredientTypeDisplay } from "./useIngredientTypeDisplay";
import { IngredientPickerTableV2 } from "./IngredientPickerTableV2";
import { getIngredientPickerEntityKey, getProductIngredientKey, toProductIngredient } from "./useIngredientPickerRows";
import type { IngredientPickerEntity, IngredientPickerSource } from "./useIngredientPickerRows";

type IngredientsSearchModalV2Props = {
    open: boolean;
    onClose: () => void;
    productIngredients: ProductFormProductIngredient[];
    editingIndex?: number | null;
    onSelect: (ingredient: ProductFormProductIngredient) => void;
};

export const IngredientsSearchModalV2 = (props: IngredientsSearchModalV2Props) => {
    const { open, onClose, productIngredients, editingIndex = null, onSelect } = props;
    const { t } = useTranslation();
    const ingredientTypes = useIngredientTypeDisplay();
    const { isFeatureActivated } = useApplicationParameters();
    const search = useDelayedSearch();
    const [source, setSource] = useState<IngredientPickerSource>("raw-material");
    const canPickIntermediateProducts = isFeatureActivated("FS-entity-intermediate-products");
    const selectedIngredientKeys = useMemo(() => {
        const keys = productIngredients.map(getProductIngredientKey);

        if (editingIndex !== null) {
            keys.splice(editingIndex, 1);
        }

        return new Set(keys);
    }, [editingIndex, productIngredients]);
    const isEditing = editingIndex !== null;
    const handleClose = () => {
        search.resetSearch();
        setSource("raw-material");
        onClose();
    };

    const handleSourceChange = (nextSource: IngredientPickerSource) => {
        search.resetSearch();
        setSource(nextSource);
    };

    const handleSelect = (entity: IngredientPickerEntity) => {
        if (!selectedIngredientKeys.has(getIngredientPickerEntityKey(entity))) {
            onSelect(toProductIngredient(entity));
        }

        handleClose();
    };

    const sourceTabs = [
        {
            ...ingredientTypes["raw-material"],
            value: "raw-material" as const,
        },
        ...(canPickIntermediateProducts ?
            [
                {
                    ...ingredientTypes["intermediate-product"],
                    value: "intermediate-product" as const,
                },
            ]
        :   []),
    ];
    const searchPlaceholder =
        source === "intermediate-product" ?
            t("product.v2.recipe.intermediate_product_search_placeholder", "Search for an intermediate product...")
        :   t("product.v2.recipe.raw_material_search_placeholder", "Search for a raw material...");

    return (
        <ReferenceSearchDialog
            closeLabel={t("common.close", "Close")}
            maxHeight={728}
            onClose={handleClose}
            onSearchChange={search.setSearch}
            open={open}
            searchPlaceholder={searchPlaceholder}
            searchValue={search.search}
            title={
                isEditing ?
                    t("product.v2.recipe.change_ingredient", "Change ingredient")
                :   t("product.v2.recipe.add_ingredient", "Add an ingredient")
            }
            toolbar={
                <ReferenceSourceTabs
                    ariaLabel={t("product.v2.recipe.ingredient_source", "Ingredient source")}
                    onChange={handleSourceChange}
                    tabs={sourceTabs}
                    value={source}
                />
            }
        >
            <IngredientPickerTableV2
                delayedSearch={search.delayedSearch}
                onSelect={handleSelect}
                selectedIngredientKeys={selectedIngredientKeys}
                source={source}
            />
        </ReferenceSearchDialog>
    );
};
